/**
 * Format a salary range for display, or return `null` when it isn't specified.
 *
 * Treats 0 / "0.00" / null / undefined / negative / NaN as "not specified" — a
 * zero salary is never real, and the detail API returns amounts as strings
 * (e.g. "0.00") which are truthy, so callers must not rely on plain truthiness.
 */
export function formatSalary(
  min?: number | string | null,
  max?: number | string | null,
  currency = 'USD'
): string | null {
  const fmt = (n: number) => {
    try {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency,
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(n);
    } catch {
      return `${n}`;
    }
  };

  const lo = Number(min);
  const hi = Number(max);
  const hasLo = Number.isFinite(lo) && lo > 0;
  const hasHi = Number.isFinite(hi) && hi > 0;
  if (hasLo && hasHi) return `${fmt(lo)} – ${fmt(hi)}`;
  if (hasLo) return `${fmt(lo)}+`;
  if (hasHi) return `Up to ${fmt(hi)}`;
  return null;
}
